import {
    BaseButton,
    ClaimTimelineCardStyles as Card,
    DocumentViewer,
    ProviderDocumentUrl,
    ProviderInstitutionalClaim,
    ProviderInstitutionalClaimTemplate,
    ProviderNynf3Claim,
    ProviderNynf3ClaimTemplate,
    ProviderPayerSubscriber,
    ProviderProfessionalClaim,
    ProviderProfessionalClaimTemplate,
    ProviderSubscriberPatient,
    Spinner,
    formatClaimTemplateServiceLines,
} from '@enter/core';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DialogContext } from 'services';
import { RootStore } from 'stores';
import { ClaimService } from 'stores/claim.service';
import { ICms1500Selection, showCms1500Dialog } from '../cms1500-dialog/cms1500-dialog';
import { findSubscriberPatientFor } from './cms1500-selection';

interface IClaimDetailProps {
    claimId?: string;
    performedBy?: string;
    claimPind: ProviderProfessionalClaim | ProviderInstitutionalClaim | ProviderNynf3Claim;
    payerSubscribers: ProviderPayerSubscriber[] | undefined;
    subscriberPatients: ProviderSubscriberPatient[] | undefined;
    template?: ProviderProfessionalClaimTemplate | ProviderInstitutionalClaimTemplate | ProviderNynf3ClaimTemplate;
}

export const ClaimDetail: React.FunctionComponent<IClaimDetailProps> = inject('rootStore')(
    observer(
        (props: IClaimDetailProps): React.ReactElement => {
            const rootStore: RootStore = _.get(props, 'rootStore');

            if (!rootStore) {
                return <></>;
            }

            const dialogContext = useContext(DialogContext);
            const { claimId, performedBy, claimPind, template } = props;
            const payerSubscribers = useMemo(() => props.payerSubscribers || [], [props.payerSubscribers]);
            const subscriberPatients = useMemo(() => props.subscriberPatients || [], [props.subscriberPatients]);
            // Primary payer subscriber until the user picks another one in the dialog.
            const [selection, setSelection] = useState<ICms1500Selection | undefined>();
            const payerSubscriber = selection
                ? payerSubscribers.find(el => el.id === selection.payerSubscriberId)
                : _.first(payerSubscribers);
            const subscriberPatient = useMemo(
                () =>
                    selection
                        ? subscriberPatients.find(el => el.id === selection.subscriberPatientId)
                        : findSubscriberPatientFor(_.first(payerSubscribers), payerSubscribers, subscriberPatients),
                [selection, payerSubscribers, subscriberPatients],
            );
            const editable = selection?.editable;
            // Read through a ref so a new dialog context (created by showError itself) never re-runs the fetch.
            const showErrorRef = useRef(dialogContext.showError);
            showErrorRef.current = dialogContext.showError;
            const [loading, setLoading] = useState(false);
            const [documentUrl, setDocumentUrl] = useState<ProviderDocumentUrl | undefined>();
            const isInstitutional = !!(claimPind as ProviderInstitutionalClaim).institutionalTemplateId;
            const missingRelationship = !isInstitutional && (!payerSubscriber?.id || !subscriberPatient?.id);
            const serviceLines = useMemo(() => formatClaimTemplateServiceLines(template), [template]);

            useEffect(() => {
                // Only the effect that is still current may publish: a superseded request that finishes late
                // must not replace the selected payer's form or clear the newer request's loading state.
                let active = true;

                (async (): Promise<void> => {
                    if (!claimPind?.id) {
                        return;
                    }

                    // No matching relationship is an empty state, not an error: showError re-creates the dialog
                    // context, which would re-run this effect and raise the error again, indefinitely.
                    if (!isInstitutional && (!payerSubscriber?.id || !subscriberPatient?.id)) {
                        setDocumentUrl(undefined);
                        setLoading(false);
                        return;
                    }

                    try {
                        setLoading(true);

                        const nextDocumentUrl = isInstitutional
                            ? await ClaimService.viewCMS1450({ id: claimPind.id })
                            : await ClaimService.viewCMS1500({
                                  id: claimPind.id,
                                  payerSubscriberId: payerSubscriber?.id,
                                  subscriberPatientId: subscriberPatient?.id,
                                  editable,
                              });

                        if (active) {
                            setDocumentUrl(nextDocumentUrl);
                        }
                    } catch (err) {
                        if (active) {
                            setDocumentUrl(undefined);
                            showErrorRef.current(err);
                        }
                    } finally {
                        if (active) {
                            setLoading(false);
                        }
                    }
                })();

                return () => {
                    active = false;
                };
            }, [
                claimPind?.id,
                isInstitutional,
                payerSubscriber?.id,
                subscriberPatient?.id,
                editable,
            ]);

            return (
                <Card.CdPanel $embedded>
                    <Card.CdSectionCard $embedded>
                        <Card.CdFactsGrid>
                            {claimId && (
                                <Card.CdFactCell>
                                    <Card.CdFactLabel>Claim ID</Card.CdFactLabel>
                                    <Card.CdFactValue $mono>{claimId}</Card.CdFactValue>
                                </Card.CdFactCell>
                            )}
                            <Card.CdFactCell $span={2}>
                                <Card.CdFactLabel>Service lines</Card.CdFactLabel>
                                <Card.CdFactValue>{serviceLines || '—'}</Card.CdFactValue>
                            </Card.CdFactCell>
                            {performedBy && (
                                <Card.CdFactCell>
                                    <Card.CdFactLabel>Performed by</Card.CdFactLabel>
                                    <Card.CdFactValue>{performedBy}</Card.CdFactValue>
                                </Card.CdFactCell>
                            )}
                        </Card.CdFactsGrid>
                    </Card.CdSectionCard>

                    <Card.CdSectionCard $embedded>
                        {!isInstitutional && payerSubscribers.length > 0 && (
                            <BaseButton
                                variant="outlined"
                                size="compact"
                                type="button"
                                disabled={loading}
                                onClick={() =>
                                    showCms1500Dialog(dialogContext, {
                                        payerSubscribers,
                                        subscriberPatients,
                                        defaultPayerSubscriberId: payerSubscriber?.id,
                                        onConfirm: setSelection,
                                    })
                                }>
                                Choose payer / patient
                            </BaseButton>
                        )}
                        {missingRelationship && (
                            <Card.CdFactLabel>
                                No subscriber patient is linked to this payer subscriber. Choose a payer / patient to
                                view the CMS-1500.
                            </Card.CdFactLabel>
                        )}
                        {loading ? <Spinner /> : documentUrl?.url && <DocumentViewer url={documentUrl.url} />}
                    </Card.CdSectionCard>
                </Card.CdPanel>
            );
        },
    ),
);
